import React, { useState } from 'react';
import {
  ArrowLeft, Building2, Users, CreditCard, Edit2, Save, X,
  Calendar, Mail, Activity, Plus, Trash2, RefreshCw, AlertCircle, Shield
} from 'lucide-react';
import GoldenStandardsManager from './GoldenStandardsManager';

const TenantDetail = ({
  tenant,
  users,
  licenses,
  activeLicense,
  userCount,
  api,
  onUpdate,
  onAssignLicense,
  onBack,
  onRefresh
}) => {
  const [activeSubTab, setActiveSubTab] = useState('overview');
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({
    name: tenant?.name || '',
    billing_email: tenant?.billing_email || '',
    status: tenant?.status || 'active'
  });
  const [showLicenseForm, setShowLicenseForm] = useState(false);
  const [showAddUserForm, setShowAddUserForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getLicenseBadgeClass = (licenseType) => {
    switch (licenseType) {
      case 'enterprise':
        return 'license-badge-enterprise';
      case 'professional':
        return 'license-badge-professional';
      default:
        return 'license-badge-free';
    }
  };

  const handleSaveEdit = async () => {
    setLoading(true);
    setError(null);
    try {
      await onUpdate(tenant.id, editData);
      setIsEditing(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setEditData({
      name: tenant?.name || '',
      billing_email: tenant?.billing_email || '',
      status: tenant?.status || 'active'
    });
    setIsEditing(false);
    setError(null);
  };

  // License Form Component
  const LicenseForm = () => {
    const [licenseData, setLicenseData] = useState({
      license_type: 'professional',
      expires_at: '',
      max_users: '',
      max_monthly_scans: '',
      notes: ''
    });
    const [formLoading, setFormLoading] = useState(false);
    const [formError, setFormError] = useState(null);

    const handleSubmit = async (e) => {
      e.preventDefault();
      setFormLoading(true);
      setFormError(null);
      try {
        const submitData = {
          license_type: licenseData.license_type,
          notes: licenseData.notes || null
        };
        if (licenseData.expires_at) {
          submitData.expires_at = new Date(licenseData.expires_at).toISOString();
        }
        if (licenseData.max_users) {
          submitData.max_users = parseInt(licenseData.max_users);
        }
        if (licenseData.max_monthly_scans) {
          submitData.max_monthly_scans = parseInt(licenseData.max_monthly_scans);
        }
        await onAssignLicense(tenant.id, submitData);
        setShowLicenseForm(false);
      } catch (err) {
        setFormError(err.message);
      } finally {
        setFormLoading(false);
      }
    };

    return (
      <div className="modal-overlay" onClick={() => setShowLicenseForm(false)}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <div className="modal-title">
              <CreditCard size={24} color="#667eea" />
              <h2>Assign License</h2>
            </div>
            <button className="modal-close" onClick={() => setShowLicenseForm(false)}>
              <X size={20} />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="tenant-form">
            {formError && (
              <div className="form-error">
                <AlertCircle size={16} />
                <span>{formError}</span>
              </div>
            )}

            <div className="form-group">
              <label htmlFor="license_type">License Type *</label>
              <select
                id="license_type"
                value={licenseData.license_type}
                onChange={(e) => setLicenseData(prev => ({ ...prev, license_type: e.target.value }))}
                required
                disabled={formLoading}
              >
                <option value="free">Free</option>
                <option value="professional">Professional</option>
                <option value="enterprise">Enterprise</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="expires_at">Expires At (optional)</label>
              <input
                type="datetime-local"
                id="expires_at"
                value={licenseData.expires_at}
                onChange={(e) => setLicenseData(prev => ({ ...prev, expires_at: e.target.value }))}
                disabled={formLoading}
              />
              <span className="form-hint">Leave empty for no expiration</span>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="max_users">Max Users (optional)</label>
                <input
                  type="number"
                  id="max_users"
                  value={licenseData.max_users}
                  onChange={(e) => setLicenseData(prev => ({ ...prev, max_users: e.target.value }))}
                  placeholder="Defaults to license type"
                  min="1"
                  disabled={formLoading}
                />
              </div>

              <div className="form-group">
                <label htmlFor="max_monthly_scans">Max Monthly Scans (optional)</label>
                <input
                  type="number"
                  id="max_monthly_scans"
                  value={licenseData.max_monthly_scans}
                  onChange={(e) => setLicenseData(prev => ({ ...prev, max_monthly_scans: e.target.value }))}
                  placeholder="Defaults to license type"
                  min="1"
                  disabled={formLoading}
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="notes">Notes</label>
              <textarea
                id="notes"
                value={licenseData.notes}
                onChange={(e) => setLicenseData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Internal notes about this license..."
                rows={3}
                disabled={formLoading}
              />
            </div>

            <div className="form-actions">
              <button
                type="button"
                className="admin-btn admin-btn-secondary"
                onClick={() => setShowLicenseForm(false)}
                disabled={formLoading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="admin-btn admin-btn-primary"
                disabled={formLoading}
              >
                <Save size={16} />
                {formLoading ? 'Saving...' : 'Assign License'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  // Add User Form Component
  const AddUserForm = () => {
    const [userData, setUserData] = useState({
      user_id: '',
      user_email: '',
      role: 'member'
    });
    const [formLoading, setFormLoading] = useState(false);
    const [formError, setFormError] = useState(null);

    const handleSubmit = async (e) => {
      e.preventDefault();
      setFormLoading(true);
      setFormError(null);
      try {
        await api.addUserToTenant(tenant.id, userData);
        setShowAddUserForm(false);
        onRefresh();
      } catch (err) {
        setFormError(err.message);
      } finally {
        setFormLoading(false);
      }
    };

    return (
      <div className="modal-overlay" onClick={() => setShowAddUserForm(false)}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <div className="modal-title">
              <Users size={24} color="#667eea" />
              <h2>Add User to Tenant</h2>
            </div>
            <button className="modal-close" onClick={() => setShowAddUserForm(false)}>
              <X size={20} />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="tenant-form">
            {formError && (
              <div className="form-error">
                <AlertCircle size={16} />
                <span>{formError}</span>
              </div>
            )}

            <div className="form-group">
              <label htmlFor="user_id">User ID (Keycloak sub) *</label>
              <input
                type="text"
                id="user_id"
                value={userData.user_id}
                onChange={(e) => setUserData(prev => ({ ...prev, user_id: e.target.value }))}
                placeholder="e.g., 12345678-1234-1234-1234-123456789012"
                required
                disabled={formLoading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="user_email">User Email *</label>
              <input
                type="email"
                id="user_email"
                value={userData.user_email}
                onChange={(e) => setUserData(prev => ({ ...prev, user_email: e.target.value }))}
                placeholder="user@example.com"
                required
                disabled={formLoading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="role">Role</label>
              <select
                id="role"
                value={userData.role}
                onChange={(e) => setUserData(prev => ({ ...prev, role: e.target.value }))}
                disabled={formLoading}
              >
                <option value="member">Member</option>
                <option value="admin">Admin</option>
                <option value="owner">Owner</option>
              </select>
            </div>

            <div className="form-actions">
              <button
                type="button"
                className="admin-btn admin-btn-secondary"
                onClick={() => setShowAddUserForm(false)}
                disabled={formLoading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="admin-btn admin-btn-primary"
                disabled={formLoading}
              >
                <Plus size={16} />
                {formLoading ? 'Adding...' : 'Add User'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  const handleRemoveUser = async (userId) => {
    if (!window.confirm('Are you sure you want to remove this user from the tenant?')) {
      return;
    }
    try {
      await api.removeUserFromTenant(tenant.id, userId);
      onRefresh();
    } catch (err) {
      alert(`Error: ${err.message}`);
    }
  };

  return (
    <div className="tenant-detail">
      {/* Header */}
      <div className="tenant-detail-header">
        <button className="back-btn" onClick={onBack}>
          <ArrowLeft size={20} />
          Back to Tenants
        </button>

        <div className="tenant-detail-title">
          <Building2 size={28} color="#667eea" />
          {isEditing ? (
            <input
              type="text"
              value={editData.name}
              onChange={(e) => setEditData(prev => ({ ...prev, name: e.target.value }))}
              className="inline-edit-input"
            />
          ) : (
            <h1>{tenant?.name}</h1>
          )}
          <span className={`status-badge status-badge-${tenant?.status}`}>
            {tenant?.status}
          </span>
        </div>

        <div className="tenant-detail-actions">
          <button
            className="admin-btn admin-btn-secondary"
            onClick={onRefresh}
          >
            <RefreshCw size={16} />
            Refresh
          </button>
          {isEditing ? (
            <>
              <button
                className="admin-btn admin-btn-secondary"
                onClick={handleCancelEdit}
              >
                <X size={16} />
                Cancel
              </button>
              <button
                className="admin-btn admin-btn-primary"
                onClick={handleSaveEdit}
                disabled={loading}
              >
                <Save size={16} />
                {loading ? 'Saving...' : 'Save'}
              </button>
            </>
          ) : (
            <button
              className="admin-btn admin-btn-primary"
              onClick={() => setIsEditing(true)}
            >
              <Edit2 size={16} />
              Edit
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="form-error" style={{ margin: '1rem 0' }}>
          <AlertCircle size={16} />
          <span>{error}</span>
        </div>
      )}

      {/* Sub Tabs */}
      <div className="tenant-sub-tabs">
        <button
          className={`tenant-sub-tab ${activeSubTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('overview')}
        >
          <Activity size={16} />
          Overview
        </button>
        <button
          className={`tenant-sub-tab ${activeSubTab === 'users' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('users')}
        >
          <Users size={16} />
          Users ({userCount || 0})
        </button>
        <button
          className={`tenant-sub-tab ${activeSubTab === 'licenses' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('licenses')}
        >
          <CreditCard size={16} />
          Licenses
        </button>
        <button
          className={`tenant-sub-tab ${activeSubTab === 'golden-standards' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('golden-standards')}
        >
          <Shield size={16} />
          Golden Standards
        </button>
      </div>

      {/* Content */}
      <div className="tenant-detail-content">
        {activeSubTab === 'overview' && (
          <div className="tenant-overview">
            <div className="info-grid">
              <div className="info-card">
                <div className="info-card-header">
                  <Building2 size={18} />
                  <h3>Tenant Information</h3>
                </div>
                <div className="info-card-body">
                  <div className="info-row">
                    <span className="info-label">ID</span>
                    <span className="info-value code">{tenant?.id}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Slug</span>
                    <span className="info-value code">{tenant?.slug}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Billing Email</span>
                    {isEditing ? (
                      <input
                        type="email"
                        value={editData.billing_email}
                        onChange={(e) => setEditData(prev => ({ ...prev, billing_email: e.target.value }))}
                        className="inline-edit-input small"
                      />
                    ) : (
                      <span className="info-value">{tenant?.billing_email || 'Not set'}</span>
                    )}
                  </div>
                  <div className="info-row">
                    <span className="info-label">Status</span>
                    {isEditing ? (
                      <select
                        value={editData.status}
                        onChange={(e) => setEditData(prev => ({ ...prev, status: e.target.value }))}
                        className="inline-edit-select"
                      >
                        <option value="active">Active</option>
                        <option value="trial">Trial</option>
                        <option value="suspended">Suspended</option>
                      </select>
                    ) : (
                      <span className={`status-badge status-badge-${tenant?.status}`}>
                        {tenant?.status}
                      </span>
                    )}
                  </div>
                  <div className="info-row">
                    <span className="info-label">Created</span>
                    <span className="info-value">{formatDate(tenant?.created_at)}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Updated</span>
                    <span className="info-value">{formatDate(tenant?.updated_at)}</span>
                  </div>
                </div>
              </div>

              <div className="info-card">
                <div className="info-card-header">
                  <CreditCard size={18} />
                  <h3>Active License</h3>
                </div>
                <div className="info-card-body">
                  {activeLicense ? (
                    <>
                      <div className="info-row">
                        <span className="info-label">Type</span>
                        <span className={`license-badge ${getLicenseBadgeClass(activeLicense.license_type)}`}>
                          {activeLicense.license_type}
                        </span>
                      </div>
                      <div className="info-row">
                        <span className="info-label">Started</span>
                        <span className="info-value">{formatDate(activeLicense.starts_at)}</span>
                      </div>
                      <div className="info-row">
                        <span className="info-label">Expires</span>
                        <span className="info-value">{activeLicense.expires_at ? formatDate(activeLicense.expires_at) : 'Never'}</span>
                      </div>
                      <div className="info-row">
                        <span className="info-label">Max Users</span>
                        <span className="info-value">{activeLicense.max_users || 'Unlimited'}</span>
                      </div>
                      <div className="info-row">
                        <span className="info-label">Max Scans/Month</span>
                        <span className="info-value">{activeLicense.max_monthly_scans || 'Unlimited'}</span>
                      </div>
                    </>
                  ) : (
                    <div className="no-license">
                      <p>No active license</p>
                      <button
                        className="admin-btn admin-btn-primary"
                        onClick={() => setShowLicenseForm(true)}
                      >
                        <Plus size={16} />
                        Assign License
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeSubTab === 'users' && (
          <div className="tenant-users">
            <div className="section-header">
              <h3>Users ({userCount || 0})</h3>
              <button
                className="admin-btn admin-btn-primary"
                onClick={() => setShowAddUserForm(true)}
              >
                <Plus size={16} />
                Add User
              </button>
            </div>

            {users && users.length > 0 ? (
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Joined</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.id}>
                      <td>
                        <div className="user-cell">
                          <Mail size={14} />
                          {user.user_email}
                        </div>
                      </td>
                      <td>
                        <span className={`role-badge role-badge-${user.role}`}>
                          {user.role}
                        </span>
                      </td>
                      <td>{formatDate(user.joined_at)}</td>
                      <td>
                        <button
                          className="table-action-btn"
                          onClick={() => handleRemoveUser(user.user_id)}
                          title="Remove user"
                        >
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="empty-state">
                <Users size={48} color="#a0aec0" />
                <p>No users in this tenant</p>
              </div>
            )}
          </div>
        )}

        {activeSubTab === 'licenses' && (
          <div className="tenant-licenses">
            <div className="section-header">
              <h3>License History</h3>
              <button
                className="admin-btn admin-btn-primary"
                onClick={() => setShowLicenseForm(true)}
              >
                <Plus size={16} />
                Assign New License
              </button>
            </div>

            {licenses && licenses.length > 0 ? (
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Status</th>
                    <th>Starts</th>
                    <th>Expires</th>
                    <th>Max Users</th>
                    <th>Max Scans</th>
                    <th>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {licenses.map((license) => (
                    <tr key={license.id} className={!license.is_active ? 'inactive-row' : ''}>
                      <td>
                        <span className={`license-badge ${getLicenseBadgeClass(license.license_type)}`}>
                          {license.license_type}
                        </span>
                      </td>
                      <td>
                        <span className={`status-badge ${license.is_active ? 'status-badge-active' : 'status-badge-suspended'}`}>
                          {license.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>{formatDate(license.starts_at)}</td>
                      <td>{license.expires_at ? formatDate(license.expires_at) : 'Never'}</td>
                      <td>{license.max_users || 'Unlimited'}</td>
                      <td>{license.max_monthly_scans || 'Unlimited'}</td>
                      <td>{license.notes || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="empty-state">
                <CreditCard size={48} color="#a0aec0" />
                <p>No licenses assigned</p>
              </div>
            )}
          </div>
        )}

        {activeSubTab === 'golden-standards' && (
          <GoldenStandardsManager
            api={api}
            tenantId={tenant?.id}
            tenantName={tenant?.name}
            onBack={() => setActiveSubTab('overview')}
          />
        )}
      </div>

      {/* Modals */}
      {showLicenseForm && <LicenseForm />}
      {showAddUserForm && <AddUserForm />}
    </div>
  );
};

export default TenantDetail;
